import type { PricingLine } from '@/composables/booking/useBookingPricing';
import type {
  AvailabilityCalendarRequest,
  AvailabilityCalendarResponse,
  AvailabilityRequest,
  AvailabilityResponse,
  OctoUnit,
} from '@/types/booking-api';
import type {
  AddToCartRequest,
  CartDetailsResponse,
  CartResponse,
  CreateGuestCustomerRequest,
  GuestCustomerResponse,
  PartialCaptureDecision,
  PartialFailureInfo,
  ResumeTransactionResponse,
  SaveParticipantsRequest,
  StartTransactionRequest,
  StartTransactionResponse,
  UnitSelection,
  UpdateCustomerRequest,
} from '@/types/carts-transactions-api';

import { ITEMS_COUNT_STORAGE_KEY } from '@/types/carts-transactions-api';

export class ApiError extends Error {
  constructor(readonly status: number, readonly errorType: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

class CartsTransactionsApi {
  private readonly apiURL: string;
  private apiKey: string | null = null;

  constructor(apiURL: string) {
    this.apiURL = apiURL;
  }

  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (this.apiKey) {
      headers['X-Youbook-Api-Key'] = this.apiKey;
    }
    if (extra) {
      Object.assign(headers, extra);
    }
    return headers;
  }

  private async request<T>(
    path: string,
    options: RequestInit & { extraHeaders?: Record<string, string> },
    errorContext: string,
  ): Promise<T> {
    const url = `${this.apiURL}${path}`;
    const { extraHeaders, ...fetchOptions } = options;
    const headers = this.buildHeaders(extraHeaders);

    try {
      const response = await fetch(url, { ...fetchOptions, headers, credentials: 'include' });
      if (!response.ok) {
        let errorType = 'UnknownError';
        let errorMessage = `${errorContext}: ${response.status} ${response.statusText}`;
        try {
          const body = await response.json();
          errorType = body.error ?? errorType;
          errorMessage = body.message ?? errorMessage;
        }
        catch { /* response body was not JSON, keep defaults */ }
        throw new ApiError(response.status, errorType, errorMessage);
      }
      return await response.json() as T;
    }
    catch (err) {
      console.error(`[CartsTransactionsApi] ${errorContext}:`, err);
      throw err;
    }
  }

  private jsonBody(data: unknown): { extraHeaders: Record<string, string>; body: string } {
    return {
      extraHeaders: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    };
  }

  async fetchCartDetails(locale?: string): Promise<CartDetailsResponse | null> {
    const url = `${this.apiURL}/carts/current`;
    const headers = this.buildHeaders(locale ? { 'Accept-Language': locale } : undefined);

    try {
      const response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
      if (response.status === 404 || response.status === 401) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Cart API error: ${response.status} ${response.statusText}`);
      }
      const result: CartDetailsResponse = await response.json();

      if (result.items) {
        this.storeItemsCount(result.items.length);
      }

      return result;
    }
    catch (err) {
      console.error('[CartsTransactionsApi] Error fetching cart details:', err);
      throw err;
    }
  }

  async addToCart(
    productId: string,
    paymentProductRef: string,
    optionId: string,
    availabilityId: string,
    localDate: string,
    units: UnitSelection[],
    distributorId: string,
  ): Promise<CartResponse> {
    const request: AddToCartRequest = {
      productId,
      paymentProductRef,
      optionId,
      availabilityId,
      localDate,
      units,
      distributorId,
    };

    const result = await this.request<CartResponse>(
      '/carts/items',
      { method: 'POST', ...this.jsonBody(request) },
      'Error adding to cart',
    );

    if (result !== null && result.items && result.items.length > 0) {
      this.storeItemsCount(result.items.length);
    }

    return result;
  }

  async deleteItemFromCart(itemId: string): Promise<CartResponse> {
    const result = await this.request<CartResponse>(
      `/carts/current/items/${itemId}`,
      { method: 'DELETE' },
      'Error deleting item from cart',
    );

    if (result.items) {
      this.storeItemsCount(result.items.length);
    }

    return result;
  }

  async saveParticipants(itemId: string, request: SaveParticipantsRequest): Promise<CartResponse> {
    return this.request<CartResponse>(
      `/carts/current/items/${itemId}/participants`,
      { method: 'PUT', ...this.jsonBody(request) },
      'Error saving participants',
    );
  }

  async startTransaction(request: StartTransactionRequest): Promise<StartTransactionResponse> {
    return this.request<StartTransactionResponse>(
      '/transactions',
      { method: 'POST', ...this.jsonBody(request) },
      'Error starting transaction',
    );
  }

  async resumeTransaction(): Promise<ResumeTransactionResponse> {
    return this.request<ResumeTransactionResponse>(
      '/transactions/current/resume',
      {},
      'Error resuming transaction',
    );
  }

  getWidgetUrl(): string {
    return `${this.apiURL}/transactions/current/widget`;
  }

  getTransactionEventsUrl(): string {
    return `${this.apiURL}/transactions/current/events`;
  }

  buildUnitsFromPricingLines(pricingLines: PricingLine[]): UnitSelection[] {
    return pricingLines.map(line => ({
      id: line.unitId,
      quantity: line.quantity,
      label: line.unitLabel,
    }));
  }

  async fetchAvailabilityCalendar(
    request: AvailabilityCalendarRequest,
    locale: string,
    signal?: AbortSignal,
  ): Promise<AvailabilityCalendarResponse> {
    return this.request<AvailabilityCalendarResponse>(
      '/booking/availability/calendar',
      { method: 'POST', ...this.jsonBody(request), extraHeaders: { 'Content-Type': 'application/json', 'Accept-Language': locale }, signal },
      'Error loading availability calendar',
    );
  }

  async fetchAvailability(
    request: AvailabilityRequest,
    locale: string,
    signal?: AbortSignal,
  ): Promise<AvailabilityResponse> {
    return this.request<AvailabilityResponse>(
      '/booking/availability',
      { method: 'POST', ...this.jsonBody(request), extraHeaders: { 'Content-Type': 'application/json', 'Accept-Language': locale }, signal },
      'Error loading availability',
    );
  }

  buildUnitsFromSelection(selectedUnits: Map<string, number>): OctoUnit[] {
    const units: OctoUnit[] = [];
    selectedUnits.forEach((quantity, unitId) => {
      if (quantity > 0) {
        units.push({ id: unitId, quantity });
      }
    });
    return units;
  }

  async fetchCustomer(): Promise<GuestCustomerResponse> {
    return this.request<GuestCustomerResponse>(
      '/customers/current',
      {},
      'Error fetching customer',
    );
  }

  async createGuestCustomer(request: CreateGuestCustomerRequest): Promise<GuestCustomerResponse> {
    return this.request<GuestCustomerResponse>(
      '/customers/guest',
      { method: 'POST', ...this.jsonBody(request) },
      'Error creating guest customer',
    );
  }

  async updateCustomer(request: UpdateCustomerRequest): Promise<GuestCustomerResponse> {
    return this.request<GuestCustomerResponse>(
      '/customers/current',
      { method: 'PUT', ...this.jsonBody(request) },
      'Error updating customer',
    );
  }

  async sendPartialCaptureDecision(decision: PartialCaptureDecision): Promise<void> {
    const url = `${this.apiURL}/transactions/current/decision`;
    const headers = this.buildHeaders({ 'Content-Type': 'application/json' });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ decision }),
    });

    if (!response.ok) {
      throw new Error(`Error sending partial capture decision: ${response.status} ${response.statusText}`);
    }
  }

  async fetchPartialFailureInfo(): Promise<PartialFailureInfo | null> {
    const url = `${this.apiURL}/transactions/current/partial-failure`;
    const headers = this.buildHeaders();

    try {
      const response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Error fetching partial failure info: ${response.status} ${response.statusText}`);
      }
      return await response.json() as PartialFailureInfo;
    }
    catch (err) {
      console.error('[CartsTransactionsApi] Error fetching partial failure info:', err);
      return null;
    }
  }

  // Storage helpers

  private storeItemsCount(count: number): void {
    try {
      localStorage.setItem(ITEMS_COUNT_STORAGE_KEY, count.toString());

      window.postMessage(
        { event_id: 'cart_update', provider: 'youbook', items_count: count.toString() },
        '*',
      );
    }
    catch {
      console.warn('Unable to store items count in localStorage');
    }
  }

  async clearSession(): Promise<void> {
    const url = `${this.apiURL}/carts/current/session`;
    const headers = this.buildHeaders();

    try {
      await fetch(url, { method: 'DELETE', headers, credentials: 'include' });
    }
    catch {
      // Best-effort: cookie will expire naturally if this fails
    }
  }

  clearStoredData(): void {
    try {
      localStorage.removeItem(ITEMS_COUNT_STORAGE_KEY);
      window.postMessage(
        { event_id: 'cart_update', provider: 'youbook', items_count: '0' },
        '*',
      );
    }
    catch {
      // Ignore
    }
  }
}

export function createCartsTransactionsApi(): CartsTransactionsApi {
  let cartsTransactionsApiUrl;

  switch (window.location.hostname) {
    case 'localhost':
      cartsTransactionsApiUrl = '/api/youbook/carts';
      break;
    case 'dev.webcomponent.youbook.travel':
      cartsTransactionsApiUrl = 'http://localhost:8083';
      break;
    case 'preprod.marseille-tourisme.com':
      cartsTransactionsApiUrl = 'https://carts.api.youbook.rc-preprod.com';
      break;
    case 'components.youbook.rc-preprod.com':
      cartsTransactionsApiUrl = '/api/youbook/carts';
      break;
    default:
      // cartsTransactionsApiUrl = 'https://carts.api.youbook.travel';
      cartsTransactionsApiUrl = '/api/youbook/carts';
      break;
  }

  return new CartsTransactionsApi(cartsTransactionsApiUrl);
}

export function getStoredItemsCount(): number | null {
  try {
    const countStr = localStorage.getItem(ITEMS_COUNT_STORAGE_KEY);
    if (countStr !== null) {
      return Number.parseInt(countStr, 10);
    }
    return null;
  }
  catch {
    return null;
  }
}

export { CartsTransactionsApi };
